import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, Lock, Copy, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { btn, btnPrimary, selectCls, fieldCls, Field, errMsg, fmtDate } from "@/components/accounting/ui";
import DocPreview from "@/components/accounting/DocPreview";
import DatePick from "@/components/accounting/DatePick";

const VAT_HINTS = ["20 % USt", "10 % USt", "13 % USt", "0 % / steuerfrei", "Differenzbesteuerung", "Reverse Charge", "Ohne USt-Ausweis"];

const toForm = (d) => ({
  period: d.period, category: d.category, doc_type: d.doc_type || "", doc_date: d.doc_date || "",
  invoice_no: d.invoice_no || "", counterparty: d.counterparty || "",
  gross_amount: d.gross_amount == null ? "" : String(d.gross_amount).replace(".", ","),
  vat_info: d.vat_info || "", note: d.note || "", vehicle_id: d.vehicle_id || "", vin: d.vin || "",
});

const parseAmount = (s) => {
  const t = String(s).trim().replace(/\s|€/g, "");
  if (!t) return null;
  const n = Number(t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t);
  return Number.isFinite(n) ? n : NaN;
};

const Notices = ({ doc, periodLabel }) => (
  <div className="space-y-2">
    {doc.locked && <p className="text-xs rounded-lg bg-emerald-500/10 text-emerald-800 px-3 py-2 flex gap-2" data-testid="acc-doc-locked-note"><Lock size={13} className="shrink-0 mt-0.5" /> Übermittelt und gesperrt. Aufbewahrungspflicht bis {fmtDate(doc.retention_until)}.</p>}
    {doc.missing.length > 0 && !doc.locked && <p className="text-xs rounded-lg bg-amber-500/10 text-amber-800 px-3 py-2 flex gap-2"><AlertCircle size={13} className="shrink-0 mt-0.5" /> Fehlende Angaben: {doc.missing.join(", ")}</p>}
    {doc.duplicates.length > 0 && <p className="text-xs rounded-lg bg-brand/10 text-red-800 px-3 py-2 flex gap-2" data-testid="acc-doc-duplicate-note"><Copy size={13} className="shrink-0 mt-0.5" /> Identische Datei bereits vorhanden: {doc.duplicates.map((x) => `${x.original_filename} (${periodLabel(x.period)})`).join(", ")}</p>}
  </div>
);

export const DocDialog = ({ doc, meta, vehicles, periodLabel, onClose, onSaved, onDownload }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (doc) setF(toForm(doc)); }, [doc]);
  if (!doc || !f) return null;

  const locked = doc.locked;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const types = meta.categories.find((c) => c.id === f.category).types;
  const [y, m] = f.period.split("-");
  const last = new Date().getFullYear() + 1;
  const years = Array.from({ length: last - meta.first_year + 1 }, (_, i) => String(last - i));
  const pickVehicle = (e) => {
    const v = vehicles.find((x) => x.id === e.target.value);
    setF((x) => ({ ...x, vehicle_id: e.target.value, vin: x.vin || v?.vin || "" }));
  };

  const save = async () => {
    const amount = parseAmount(f.gross_amount);
    if (Number.isNaN(amount)) { toast.error("Bitte einen gültigen Betrag eingeben, z. B. 1.234,56"); return; }
    setSaving(true);
    try {
      const payload = { ...f, gross_amount: amount, doc_type: f.doc_type || null, doc_date: f.doc_date || null, vehicle_id: f.vehicle_id || null };
      await api.put(`/admin/accounting/docs/${doc.id}`, payload);
      toast.success(f.period !== doc.period ? `Beleg gespeichert und nach ${periodLabel(f.period)} verschoben` : "Beleg gespeichert");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Speichern fehlgeschlagen"));
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" data-testid="acc-doc-dialog">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{doc.original_filename}</DialogTitle>
          <DialogDescription>Buchhaltungsmonat {periodLabel(doc.period)} · Angaben dienen nur der Organisation, es erfolgt keine steuerliche Einstufung.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          <DocPreview doc={doc} />
          <div className="space-y-3">
            <Notices doc={doc} periodLabel={periodLabel} />
            <fieldset disabled={locked || saving} className="grid grid-cols-2 gap-3">
              <Field label="Kategorie">
                <select value={f.category} onChange={(e) => setF((x) => ({ ...x, category: e.target.value, doc_type: "" }))} className={`${selectCls} w-full`} data-testid="acc-edit-category">
                  {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Belegart">
                <select value={f.doc_type} onChange={set("doc_type")} className={`${selectCls} w-full`} data-testid="acc-edit-doctype">
                  <option value="">Bitte wählen</option>
                  {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Belegdatum">
                <DatePick value={f.doc_date} onChange={(v) => setF((x) => ({ ...x, doc_date: v }))} fallbackMonth={new Date(Number(y), Number(m) - 1, 1)} testId="acc-edit-date" />
              </Field>
              <Field label="Buchhaltungsmonat">
                <div className="flex gap-1.5">
                  <select value={m} onChange={(e) => setF((x) => ({ ...x, period: `${y}-${e.target.value}` }))} className={`${selectCls} flex-1 min-w-0 px-2`} data-testid="acc-edit-period-month">
                    {meta.months.map((n, i) => <option key={n} value={String(i + 1).padStart(2, "0")}>{n}</option>)}
                  </select>
                  <select value={y} onChange={(e) => setF((x) => ({ ...x, period: `${e.target.value}-${m}` }))} className={`${selectCls} px-2`} data-testid="acc-edit-period-year">
                    {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
                  </select>
                </div>
              </Field>
              <Field label="Rechnungsnummer (optional)"><input value={f.invoice_no} onChange={set("invoice_no")} maxLength={80} className={fieldCls} data-testid="acc-edit-invoice" /></Field>
              <Field label={f.category === "verkauf" ? "Kunde (optional)" : "Lieferant / Bank (optional)"}><input value={f.counterparty} onChange={set("counterparty")} maxLength={160} className={fieldCls} data-testid="acc-edit-counterparty" /></Field>
              <Field label="Bruttobetrag in € (optional)"><input value={f.gross_amount} onChange={set("gross_amount")} inputMode="decimal" placeholder="1.234,56" className={fieldCls} data-testid="acc-edit-amount" /></Field>
              <Field label="USt-Information (optional)">
                <input value={f.vat_info} onChange={set("vat_info")} list="acc-vat-hints" maxLength={120} className={fieldCls} data-testid="acc-edit-vat" />
                <datalist id="acc-vat-hints">{VAT_HINTS.map((v) => <option key={v} value={v} />)}</datalist>
              </Field>
              <Field label="Fahrzeugbezug (optional)">
                <select value={f.vehicle_id} onChange={pickVehicle} className={`${selectCls} w-full`} data-testid="acc-edit-vehicle">
                  <option value="">Kein Fahrzeug</option>
                  {vehicles.map((v) => <option key={v.id} value={v.id}>{v.title}{v.sold ? " (verkauft)" : ""}</option>)}
                </select>
              </Field>
              <Field label="FIN / VIN (optional)"><input value={f.vin} onChange={set("vin")} maxLength={24} className={`${fieldCls} uppercase`} data-testid="acc-edit-vin" /></Field>
              <Field label="Notiz" className="col-span-2"><textarea value={f.note} onChange={set("note")} rows={3} maxLength={2000} className={`${fieldCls} resize-y`} data-testid="acc-edit-note" /></Field>
            </fieldset>
            <div className="flex flex-wrap gap-2 justify-end pt-1">
              <button type="button" onClick={() => onDownload(doc)} className={btn} data-testid="acc-edit-download-btn"><Download size={13} /> Original herunterladen</button>
              <button type="button" onClick={onClose} disabled={saving} className={btn} data-testid="acc-edit-cancel-btn">{locked ? "Schließen" : "Abbrechen"}</button>
              {!locked && (
                <button type="button" onClick={save} disabled={saving} className={btnPrimary} data-testid="acc-edit-save-btn">
                  {saving && <Loader2 size={14} className="animate-spin" />} Speichern
                </button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocDialog;
